import type {
  CoverageCategory,
  FormValues,
  MutationPayload,
  QueryContextKey,
  QueryData,
  ScheduleStats,
  SortField,
  SortOrder,
  Group,
  Location
} from './types'
import type { TableColumnsType } from 'antd'
import type { URLSearchParamsInit } from 'react-router-dom'

import {
  CalendarOutlined,
  EllipsisOutlined,
  InfoCircleOutlined,
  SearchOutlined
} from '@ant-design/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Spin,
  Table,
  Tag,
  Tooltip,
  notification
} from 'antd'
import { AxiosError, isAxiosError } from 'axios'
import classNames from 'classnames'
import cleanDeep from 'clean-deep'
import { format, isSameDay, isValid, parseISO, startOfDay } from 'date-fns'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useBlocker, useSearchParams } from 'react-router-dom'
import { useDebounce } from 'react-use'

import $api from '../../../API'
import BarsIcon from '../../../assets/icons/bars.svg?react'
import { CRReportType } from '../../../domain/cr-report-type.enum'

import { Bars } from './bar'
import { ON_HOLD, EMPTY } from './constants'
import { ExpandIcon } from './expand-icon'
import { ExpandedRow } from './expanded-row'
import {
  ensureRequiredReportConfigs,
  getIsFormDirty,
  humanReadableSchedule,
  isEmptySchedule
} from './functions'
import { SchedulePreviewCard } from './scheduler-preview-card/scheduler-preview-card'
import { initialState, reducer } from './state'

export function GroupsSchedulerPage(): JSX.Element {
  const [form] = Form.useForm<FormValues>()
  const [initialFormValues, setInitialFormValues] = useState<FormValues>()
  const [searchParams, setSearchParams] = useSearchParams()

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname
  )

  const [state, dispatch] = useReducer(reducer, initialState, state => {
    const expandedRowId = searchParams.get('expanded-row-id')
    const query = searchParams.get('query')
    const schedule = searchParams.get('schedule')

    const isExpiring = searchParams.get('is-expiring')

    const locations = searchParams.getAll('locations')
    const coverageCategories = searchParams.getAll('coverage-categories')

    const pageSize = searchParams.get('page-size')
    const currentPage = searchParams.get('page')
    const sortField = searchParams.get('order-by')
    const sortOrder = searchParams.get('direction')

    state.searchParams.schedule = new Date().getDay()

    if (typeof expandedRowId === 'string' && expandedRowId.length > 0) {
      state.expandedRow.id = expandedRowId
    }

    if (typeof query === 'string' && query.trim().length > 0) {
      state.searchParams.query = query.trim()
    }

    if (typeof schedule === 'string' && schedule.trim().length > 0) {
      const temp =
        schedule === ON_HOLD || schedule === EMPTY
          ? schedule
          : Number.parseInt(schedule.trim(), 10) || undefined
      state.searchParams.schedule = temp === 'null' ? undefined : temp
    }

    if (isExpiring === 'true') {
      state.searchParams.flags = [...state.searchParams.flags, 'expiring']
        .sort()
        .filter((v, i, a) => a.indexOf(v) === i)
    }

    if (locations.length > 0) {
      state.searchParams.locations = locations.filter(location => location.length > 0)
    }

    if (coverageCategories.length > 0) {
      state.searchParams.coverageCategories = coverageCategories.filter(
        coverageCategory => coverageCategory.length > 0
      )
    }

    if (typeof pageSize === 'string' && pageSize.length > 0) {
      state.pagination.pageSize = Number.parseInt(pageSize, 10) || state.pagination.pageSize
    }

    if (typeof currentPage === 'string' && currentPage.length > 0) {
      const parsed = Number.parseInt(currentPage, 10)
      state.pagination.currentPage = Number.isNaN(parsed)
        ? state.pagination.currentPage
        : parsed - 1
    }

    if (typeof sortField === 'string' && sortField.length > 0) {
      state.sort.field = sortField as SortField
    }

    if (typeof sortOrder === 'string' && sortOrder.length > 0) {
      state.sort.order = sortOrder as SortOrder
    }

    return state
  })

  const [debouncedQuery, setDebouncedQuery] = useState(state.searchParams.query)

  useDebounce(
    () => {
      const query = state.searchParams.query.trim().toLowerCase()

      setDebouncedQuery(query)
      dispatch({ type: 'set-pagination', payload: { ...state.pagination, currentPage: 0 } })
    },
    500,
    [state.searchParams.query]
  )

  useEffect(() => {
    const expandedRowId = state.expandedRow.id

    const init: URLSearchParamsInit = []

    if (state.pagination.pageSize !== initialState.pagination.pageSize) {
      init.push(['page-size', state.pagination.pageSize.toString()])
    }

    if (state.pagination.currentPage > 0) {
      init.push(['page', (state.pagination.currentPage + 1).toString()])
    }

    if (debouncedQuery.length > 0) {
      init.push(['query', debouncedQuery])
    }

    if (state.searchParams.schedule !== undefined) {
      init.push(['schedule', state.searchParams.schedule.toString()])
    }

    if (state.searchParams.flags.includes('expiring')) {
      init.push(['is-expiring', 'true'])
    }

    if (state.searchParams.flags.includes('network')) {
      init.push(['is-network', 'true'])
    }

    if (typeof expandedRowId === 'string') {
      init.push(['expanded-row-id', expandedRowId])
    }

    for (const location of state.searchParams.locations) {
      init.push(['locations', location])
    }

    for (const coverageCategory of state.searchParams.coverageCategories) {
      init.push(['coverage-categories', coverageCategory])
    }

    if (typeof state.sort.field === 'string') {
      init.push(['order-by', state.sort.field])
    }

    if (typeof state.sort.order === 'string') {
      init.push(['direction', state.sort.order])
    }

    setSearchParams(init, { replace: true })
  }, [
    state.sort,
    state.pagination,
    state.searchParams,
    state.expandedRow,
    debouncedQuery,
    setSearchParams
  ])

  const query = useQuery<unknown, unknown, QueryData, QueryContextKey>({
    queryKey: [
      'groups',
      state.sort,
      state.pagination,
      { ...state.searchParams, query: debouncedQuery }
    ],

    async queryFn(context) {
      const [, sort, pagination, searchParams] = context.queryKey
      const { pageSize, currentPage } = pagination
      const { query, locations, schedule, coverageCategories, flags } = searchParams

      const searchFields = [
        'name',
        'number',
        'rx_company',
        'account_executive',
        'account_manager',
        'sales_executive',
        'group_contact',
        'funding',
        'notes'
      ]

      const sp = new URLSearchParams([
        ...(currentPage > 0 ? [['page', currentPage.toString(10)]] : []),
        ['page-size', pageSize.toString(10)],
        ...(typeof sort.field === 'string' ? [['order-by', sort.field]] : []),
        ...(typeof sort.order === 'string' ? [['direction', sort.order]] : []),
        ...locations.map(location => ['locations', location]),
        ...coverageCategories.map(coverageCategory => ['coverage-categories', coverageCategory]),
        ...(query.length > 0
          ? [['query', query], ...searchFields.map(name => ['search-fields', name])]
          : []),
        ...(schedule === undefined ? [] : [['schedule', schedule.toString()]]),
        ...(flags.includes('expiring') ? [['is-expiring', 'true']] : []),
        ...(flags.includes('network') ? [['is-network', 'true']] : []),
        ['is_epay', 'true']
      ])

      const { data } = await $api.get<{
        rows: Group[]
        total_filtered: number
        total_unfiltered: number
      }>('/groups', {
        params: sp
      })

      return {
        groups: data.rows,
        totalFiltered: data.total_filtered,
        totalUnfiltered: data.total_unfiltered
      }
    },

    onSuccess(data) {
      dispatch({ type: 'clear-expanded-row-id' })

      if (
        data.groups.length === 0 &&
        data.totalUnfiltered > 0 &&
        state.searchParams.query &&
        state.searchParams.schedule !== undefined
      ) {
        dispatch({
          type: 'set-filters',
          payload: {
            ...state.searchParams,
            schedule: undefined
          }
        })
      }
    }
  })

  const mutation = useMutation({
    async mutationFn({ id, ...data }: MutationPayload) {
      try {
        await $api.patch(`/groups/schedule/${id}`, data)

        dispatch({ type: 'clear-expanded-row-id' })

        void query.refetch()
        void scheduleStatsQuery.refetch()
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 422) {
          notification.error({
            message: 'Failed to update group',
            description: 'Validation failed'
          })

          return
        }

        throw new Error('Failed to update group')
      }
    },

    onError() {
      notification.error({
        message: 'Failed to update group'
      })
    }
  })

  const locationsQuery = useQuery<unknown, unknown, Location[]>({
    queryKey: ['locations'],

    async queryFn() {
      try {
        const response = await $api.get('/locations')
        return response.data
      } catch (e) {
        if (e instanceof AxiosError) {
          throw new TypeError(`Request failed with status ${e.status} (${e.message})`)
        }
      }
    }
  })

  const coverageCategoriesQuery = useQuery<unknown, unknown, CoverageCategory[]>({
    queryKey: ['coverage-categories'],

    async queryFn() {
      try {
        const response = await $api.get('/coverage-categories')
        return response.data
      } catch (e) {
        if (e instanceof AxiosError) {
          throw new TypeError(`Request failed with status ${e.status} (${e.message})`)
        }
      }
    }
  })

  const scheduleStatsQuery = useQuery<unknown, unknown, ScheduleStats>({
    queryKey: ['schedule-stats', { locationIds: state.searchParams.locations }],

    async queryFn({ queryKey }) {
      const searchParams = new URLSearchParams()
      const { locationIds } = queryKey[1] as { locationIds: string[] }

      if (Array.isArray(locationIds) && locationIds.length > 0) {
        for (const locationId of locationIds) {
          searchParams.append('location-id', locationId)
        }
      }

      try {
        const response = await $api.get(`/groups/schedule-stats?${searchParams.toString()}`)
        return response.data
      } catch (e) {
        if (e instanceof AxiosError) {
          throw new TypeError(`Request failed with status ${e.status} (${e.message})`)
        }
      }
    }
  })

  const columns = useMemo<TableColumnsType<Group>>(
    () => [
      {
        title({ sortColumns }) {
          const column = sortColumns?.find(sort => sort.column.key === 'number')

          return (
            <div className='flex gap-2 items-center justify-between pr-0.5'>
              <span>Group #</span>

              {!column?.order && (
                <BarsIcon className='text-[#b1b1b1] -scale-y-100 translate-y-[1px]' />
              )}

              {column && column.order === 'ascend' && (
                <BarsIcon className='text-[#1676ff] -scale-y-100 translate-y-[1px]' />
              )}

              {column && column.order === 'descend' && (
                <BarsIcon className='text-[#1676ff] rotate-180 -scale-100 translate-y-[1px]' />
              )}
            </div>
          )
        },
        key: 'number',
        dataIndex: 'number',
        className: 'w-20',
        sorter: true,
        sortOrder:
          state.sort.field === 'number'
            ? state.sort.order === 'asc'
              ? 'ascend'
              : 'descend'
            : undefined,
        showSorterTooltip: false,
        width: '10%'
      },
      {
        title({ sortColumns }) {
          const column = sortColumns?.find(sort => sort.column.key === 'name')

          return (
            <div className='flex gap-2 items-center justify-between pr-0.5'>
              <span>Name</span>

              {!column?.order && (
                <BarsIcon className='text-[#b1b1b1] -scale-y-100 translate-y-[1px]' />
              )}

              {column && column.order === 'ascend' && (
                <BarsIcon className='text-[#1676ff] -scale-y-100 translate-y-[1px]' />
              )}

              {column && column.order === 'descend' && (
                <BarsIcon className='text-[#1676ff] rotate-180 -scale-100 translate-y-[1px]' />
              )}
            </div>
          )
        },
        key: 'name',
        dataIndex: 'name',
        className: 'w-40',
        sorter: true,
        sortOrder:
          state.sort.field === 'name'
            ? state.sort.order === 'asc'
              ? 'ascend'
              : 'descend'
            : undefined,
        showSorterTooltip: false,
        width: '15%'
      },
      {
        title: 'Location',
        key: 'location',
        dataIndex: 'location',
        filteredValue:
          locationsQuery.status === 'success' && locationsQuery.data.length > 0
            ? state.searchParams.locations
            : undefined,
        filters:
          locationsQuery.status === 'success' && locationsQuery.data.length > 0
            ? [
                ...locationsQuery.data.map(location => ({
                  text: location.short_name,
                  value: location.id
                })),
                {
                  text: <span className='text-gray-500'>(Empty)</span>,
                  value: EMPTY
                }
              ]
            : undefined,
        width: '10%',
        render(location: Location | null) {
          if (!location) {
            return (
              <span>
                <span className='text-gray-300'>—</span>
              </span>
            )
          }

          return location.short_name
        }
      },
      {
        title: 'Coverage',
        key: 'coverageCategories',
        dataIndex: 'coverage_categories_ids',
        filteredValue:
          coverageCategoriesQuery.status === 'success' && coverageCategoriesQuery.data.length > 0
            ? state.searchParams.coverageCategories
            : undefined,
        filters:
          coverageCategoriesQuery.status === 'success' && coverageCategoriesQuery.data.length > 0
            ? [
                ...coverageCategoriesQuery.data.map(category => ({
                  text: category.name,
                  value: category.id
                })),
                {
                  text: <span className='text-gray-500'>(Empty)</span>,
                  value: EMPTY
                }
              ]
            : undefined,
        width: '15%',
        className: 'text-left',
        render(value: string[]) {
          if (coverageCategoriesQuery.status !== 'success') {
            return null
          }

          if (value.length === 0) {
            return <span className='text-gray-300 px-2 py-1'>—</span>
          }

          const categories = value
            .map(id => coverageCategoriesQuery.data.find(category => category.id === id))
            .filter<CoverageCategory>((v): v is CoverageCategory => v !== undefined)
            .sort((a, b) => a.name.localeCompare(b.name))

          return (
            <div className='flex flex-wrap gap-1 px-2 py-1'>
              {categories.map(category => (
                <Tag key={category.id} color='blue' className='min-w-[3rem] text-center'>
                  {category.name}
                </Tag>
              ))}
            </div>
          )
        }
      },
      {
        title: 'Schedule',
        key: 'schedule',
        dataIndex: 'schedule',
        width: '25%',
        render(value: Record<string, number[]> | null, record) {
          if (!value || isEmptySchedule(value)) {
            return <span className='text-gray-300'>—</span>
          }

          const schedule = humanReadableSchedule(value)

          if (record.is_on_hold) {
            return (
              <span className='text-gray-400'>
                On Hold: {schedule.slice(0, 1).toLowerCase() + schedule.slice(1)}
              </span>
            )
          }

          return schedule
        }
      },
      {
        title: 'Flags',
        key: 'flags',
        dataIndex: 'flags',
        align: 'center',

        filters: [
          {
            text: 'Expiring',
            value: 'expiring'
          },
          {
            text: 'Network',
            value: 'network'
          }
        ],

        filterMultiple: true,
        filteredValue: state.searchParams.flags,
        width: '5%',

        render(_, record) {
          if (record.expiration_warning) {
            const date = parseISO(record.expiration_warning.date)

            if (!isValid(date)) {
              return null
            }

            const today = isSameDay(date, new Date())
            const past = date < startOfDay(new Date()) && !today

            const formatted = today ? 'today' : `on ${format(date, 'MMM d, yyyy')}`

            const message =
              record.expiration_warning.type === 'run-out'
                ? past
                  ? `Ran out ${formatted}`
                  : `Will run out ${formatted}`
                : record.expiration_warning.type === 'termination'
                  ? past
                    ? `Terminated ${formatted}`
                    : `Will terminate ${formatted}`
                  : past
                    ? `Expired ${formatted}`
                    : `Expiring ${formatted}`

            return (
              <Tooltip title={message}>
                <CalendarOutlined
                  className={classNames(
                    'text-base',
                    !past && 'text-orange-500',
                    past && 'text-red-500'
                  )}
                />
              </Tooltip>
            )
          }

          return null
        }
      },
      {
        title: 'Notes',
        key: 'notes',
        dataIndex: 'notes',
        render(value: string | null) {
          if (value == null) {
            return (
              <span>
                <span className='text-gray-300'>—</span>
              </span>
            )
          }

          return value
        }
      }
    ],
    [
      locationsQuery.status,
      locationsQuery.data,
      coverageCategoriesQuery.status,
      coverageCategoriesQuery.data,
      state.searchParams.locations,
      state.searchParams.coverageCategories,
      state.searchParams.flags,
      state.sort.field,
      state.sort.order
    ]
  )

  const availableCoverageCategories = useMemo(() => {
    let availableCoverageCategories: CoverageCategory[] = []

    if (coverageCategoriesQuery.status === 'success' && state.expandedRow.id !== undefined) {
      const group = query.data?.groups.find(group => group.id === state.expandedRow.id)

      if (group) {
        availableCoverageCategories = group.available_coverage_categories_ids
          .map(id => ({
            paid_through: group.paid_through_coverage_categories?.find(
              category => category.id === id
            )?.paid_through,
            ...coverageCategoriesQuery.data.find(category => category.id === id)
          }))
          .filter<CoverageCategory>((v): v is CoverageCategory => v !== undefined)
      }
    }

    return availableCoverageCategories
  }, [
    coverageCategoriesQuery.data,
    coverageCategoriesQuery.status,
    query.data,
    state.expandedRow.id
  ])

  const handleSave = useCallback(() => {
    const { id } = state.expandedRow
    const values = form.getFieldsValue()

    if (id === undefined) {
      return
    }

    let schedule = cleanDeep(values.schedule) as Record<string, number[]> | null
    if (schedule && Object.keys(schedule).length === 0) {
      schedule = null
    }

    mutation.mutate({
      id,

      is_on_hold: values.isOnHold,
      is_expired: values.isExpired,

      effective_date: values.effectiveDate ? values.effectiveDate.format('YYYY-MM-DD') : null,

      renewal_date: values.renewalDate ? values.renewalDate.format('YYYY-MM-DD') : null,

      termination_date: values.terminationDate ? values.terminationDate.format('YYYY-MM-DD') : null,

      run_out_date: values.runOutDate ? values.runOutDate.format('YYYY-MM-DD') : null,

      schedule: schedule ?? null,
      rx_company: values.rxCompany.trim() || null,
      account_executive: values.accountExecutive.trim() || null,
      account_manager: values.accountManager.trim() || null,
      sales_executive: values.salesExecutive.trim() || null,
      group_contact: values.groupContact.trim() || null,
      request_for_funding: values.requestForFunding,
      release: values.release,
      funding: values.funding.trim() || null,
      notes: values.notes.trim() || null,
      coverage_categories: values.coverageCategories,

      release_type: values.releaseType ?? null,
      release_day: values.releaseType === 'auto' ? (values.releaseDay ?? null) : null,

      ignore_stop_loss: values.ignoreStopLoss,

      reports: (values.reports ?? []).map(report =>
        report.type === CRReportType.Detail
          ? {
              id: report.id,
              type: report.type,
              options: report.options
            }
          : {
              id: report.id,
              type: report.type
            }
      ),

      notification_recipients: (values.notificationRecipients ?? [])
        .map(recipient => ({
          id: recipient.id,
          email: recipient.email.trim(),
          attachments: recipient.attachments,
          delayed_notice: recipient.delayed_notice,
          is_network: false
        }))
        .filter(r => r.email.length > 0),
      network_notification_recipients: (values.networkNotificationRecipients ?? [])
        .map(recipient => ({
          id: recipient.id,
          email: recipient.email.trim(),
          attachments: recipient.attachments,
          delayed_notice: recipient.delayed_notice,
          is_network: true
        }))
        .filter(r => r.email.length > 0),
      funding_type: values.fundingType
    })
  }, [form, mutation, state.expandedRow])

  const handleCancel = useCallback(() => {
    form.resetFields()
    dispatch({ type: 'clear-expanded-row-id' })
  }, [form])

  const [isBlocked, setIsBlocked] = useState<boolean>(false)

  const lastAction = useRef<(() => void) | undefined>(undefined)

  function handleRowExpand(expanded: boolean, record: Group): void {
    form.resetFields()

    if (!expanded) {
      dispatch({ type: 'clear-expanded-row-id' })
      return
    }

    const values: FormValues = {
      id: record.id,

      isOnHold: record.is_on_hold ?? false,
      isExpired: record.is_expired ?? false,

      effectiveDate:
        typeof record.effective_date === 'string' ? dayjs(record.effective_date) : undefined,

      renewalDate: typeof record.renewal_date === 'string' ? dayjs(record.renewal_date) : undefined,

      terminationDate:
        typeof record.termination_date === 'string' ? dayjs(record.termination_date) : undefined,

      runOutDate: typeof record.run_out_date === 'string' ? dayjs(record.run_out_date) : undefined,

      schedule: record.schedule ?? undefined,
      rxCompany: record.rx_company ?? '',
      accountExecutive: record.account_executive ?? '',
      accountManager: record.account_manager ?? '',
      salesExecutive: record.sales_executive ?? '',
      groupContact: record.group_contact ?? '',
      funding: record.funding ?? '',
      requestForFunding: record.request_for_funding,
      release: record.release,
      notes: record.notes ?? '',
      coverageCategories: [...record.coverage_categories_ids],
      reports: ensureRequiredReportConfigs(record.reports),
      notificationRecipients: record.notification_recipients.filter(r => !r.is_network),
      networkNotificationRecipients: record.notification_recipients.filter(r => r.is_network),

      releaseType: record.release_type ?? undefined,
      releaseDay: record.release_day ?? undefined,
      ignoreStopLoss: record.ignore_stop_loss,
      network: record.network,
      fundingType: record.funding_type ?? null
    }

    form.setFieldsValue(values)
    setInitialFormValues(values)

    dispatch({ type: 'set-expanded-row-id', payload: record.id })
  }

  const currentFormValues = Form.useWatch([], form)

  const isDirty =
    typeof state.expandedRow.id === 'string' && getIsFormDirty(initialFormValues, currentFormValues)

  const check = useCallback<(fn: () => void) => void>(
    fn => {
      if (isDirty) {
        lastAction.current = fn
        setIsBlocked(true)
        return
      }

      fn()
    },
    [isDirty]
  )

  return (
    <>
      <Card
        className='w-full rounded-xl'
        bodyStyle={{
          padding: '1rem'
        }}
      >
        <Row className='gap-4 items-center'>
          <Col className='flex-grow'>
            <Input
              size='large'
              placeholder='Search groups, e.g. Access Services of Northern Illinois'
              className='rounded-xl'
              prefix={<SearchOutlined />}
              suffix={
                <Tooltip title='You can search groups by number or name'>
                  <InfoCircleOutlined style={{ color: 'rgba(0, 0, 0, .45)' }} />
                </Tooltip>
              }
              value={state.searchParams.query}
              onChange={event => {
                dispatch({ type: 'set-query', payload: event.target.value })
              }}
              allowClear
            />
          </Col>

          <Tooltip
            title={
              state.schedulePreview.isOpen ? 'Hide schedule preview' : 'Preview daily schedule'
            }
          >
            <Button
              htmlType='button'
              icon={<EllipsisOutlined />}
              shape='circle'
              type='text'
              onClick={() => {
                dispatch({ type: 'toggle-schedule-preview' })
              }}
            />
          </Tooltip>
        </Row>
      </Card>

      {state.schedulePreview.isOpen && (
        <SchedulePreviewCard
          onGroupSelect={group => {
            dispatch({ type: 'set-query', payload: group.number })
          }}
        />
      )}

      <Card className='w-full rounded-xl mt-4'>
        <Spin spinning={scheduleStatsQuery.isLoading}>
          <Bars
            data={scheduleStatsQuery.data}
            active={state.searchParams.schedule}
            onClick={value => {
              dispatch({
                type: 'set-filters',
                payload: {
                  ...state.searchParams,
                  schedule: state.searchParams.schedule === value ? undefined : value
                }
              })
            }}
          />
        </Spin>
      </Card>

      <Card
        className='w-full rounded-xl mt-4'
        bodyStyle={{
          padding: '0'
        }}
      >
        <Table
          size='small'
          rowKey='id'
          dataSource={query.data?.groups}
          loading={query.isLoading}
          columns={columns}
          expandable={{
            expandedRowKeys: typeof state.expandedRow.id === 'string' ? [state.expandedRow.id] : [],
            expandedRowRender(group) {
              return (
                <ExpandedRow
                  locationKey={group.location?.key}
                  form={form}
                  check={check}
                  isDirty={isDirty}
                  availableCoverageCategories={availableCoverageCategories}
                  isSubmitting={mutation.isLoading}
                  onSave={handleSave}
                  onCancel={handleCancel}
                />
              )
            },
            expandedRowClassName() {
              return '[&_.ant-table-cell]:!bg-white'
            },
            expandIcon: ExpandIcon,
            onExpand: (expanded: boolean, record: Group) => {
              check(() => {
                handleRowExpand(expanded, record)
              })
            }
          }}
          pagination={{
            size: 'default',

            pageSizeOptions: [10, 25, 50],

            current: state.pagination.currentPage + 1,
            pageSize: state.pagination.pageSize,
            total: query.data?.totalFiltered ?? 0,
            showLessItems: true,
            className: 'py-2 !pl-6 !pr-6',

            onChange(page, pageSize) {
              dispatch({
                type: 'set-pagination',
                payload: {
                  currentPage: page - 1,
                  pageSize
                }
              })
            },

            onShowSizeChange(page, pageSize) {
              dispatch({
                type: 'set-pagination',
                payload: {
                  currentPage: page - 1,
                  pageSize
                }
              })
            },

            showTotal(total, range) {
              return (
                <span className='text-sm text-gray-400'>
                  Showing groups {range[0]}–{range[1]} out of {total}
                  {query.data &&
                    query.data.totalUnfiltered !== total &&
                    ` • ${query.data.totalUnfiltered} total`}
                </span>
              )
            }
          }}
          onChange={(pagination, filters, sorter, extra) => {
            if (extra.action === 'filter') {
              dispatch({
                type: 'set-filters',
                payload: {
                  locations: (filters.location as string[] | null) ?? [],
                  coverageCategories: (filters.coverageCategories as string[] | null) ?? [],
                  flags: (filters.flags as string[] | null) ?? []
                }
              })
            }

            if (extra.action === 'sort') {
              if (Array.isArray(sorter)) {
                sorter = sorter[0] || {}
              }

              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const singleSorter = Array.isArray(sorter) ? sorter[0] : (sorter as any)
              dispatch({
                type: 'set-sort',
                payload: {
                  field: singleSorter?.column?.key as SortField,
                  order:
                    typeof singleSorter?.order === 'string'
                      ? ({ ascend: 'asc', descend: 'desc' }[
                          singleSorter?.order as 'ascend' | 'descend'
                        ] as SortOrder)
                      : undefined
                }
              })
            }
          }}
        />
      </Card>

      <Modal
        title='Are you sure you want to discard changes?'
        open={blocker.state === 'blocked' || isBlocked}
        onOk={() => {
          lastAction.current?.()
          setIsBlocked(false)
          lastAction.current = undefined as (() => void) | undefined

          blocker.proceed?.()
        }}
        onCancel={() => {
          setIsBlocked(false)
          lastAction.current = undefined as (() => void) | undefined
          blocker.reset?.()
        }}
      >
        <p>This action cannot be undone.</p>
      </Modal>
    </>
  )
}
